import { Component, DestroyRef, Input, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Widget } from 'app/campaign/widgets.model';
import { SnackBarService } from 'app/core/snack-bar.service';
import { ResponseListItem } from 'app/shared/components/response-list-selector/response-list-selector.component';
import { ResponseListsService } from 'app/shared/services/response-lists.service';
import { WidgetService } from 'app/shared/services/widget.service';

type ResponseListAction = 'new' | 'existing';

@Component({
  selector: 'app-flyout-form-settings-response-lists',
  templateUrl: './flyout-form-settings-response-lists.component.html',
  styleUrl: './flyout-form-settings-response-lists.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('expandReveal', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FlyoutFormSettingsResponseListsComponent implements OnChanges {
  @Input() public widget: Widget | null = null;

  private readonly responseListsService = inject(ResponseListsService);
  private readonly widgetService = inject(WidgetService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  public expanded = false;
  public saving = false;
  public responseLists: ResponseListItem[] | null = null;
  public responseListAction: ResponseListAction = 'new';
  public selectedResponseListId: string | null = null;
  public selectedResponseListNotChosen = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget'] && this.widget) {
      this.selectedResponseListId = this.widget.responseListId ?? null;
      this.responseListAction = this.selectedResponseListId ? 'existing' : 'new';
    }
  }

  public toggleExpansion(): void {
    this.expanded = !this.expanded;
    if (this.expanded) {
      this.loadResponseLists();
    }
  }

  public collapse(): void {
    this.expanded = false;
  }

  public onActionChange(): void {
    if (this.responseListAction === 'new') {
      this.selectedResponseListId = null;
      this.selectedResponseListNotChosen = false;
    }
  }

  public onSelectorChange(item: ResponseListItem): void {
    this.selectedResponseListId = item.id;
    this.selectedResponseListNotChosen = false;
  }

  public save(): void {
    if (!this.widget) {
      return;
    }
    if (this.responseListAction === 'existing' && !this.selectedResponseListId) {
      this.selectedResponseListNotChosen = true;
      return;
    }
    this.selectedResponseListNotChosen = false;
    // "new" sends no id, so the backend creates a fresh list.
    const responseListId = (this.responseListAction === 'existing' ? this.selectedResponseListId : undefined) as string;

    this.saving = true;
    this.responseListsService
      .saveResponseListConfiguration(this.widget.id, responseListId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.widget && this.responseListAction === 'existing') {
            this.widget.responseListId = this.selectedResponseListId ?? undefined;
          }
          this.widgetService.hasUnpublishedChangesBS.next(true);
          this.saving = false;
          this.expanded = false;
          this.snackBarService.success('generic.success');
        },
        error: () => {
          this.saving = false;
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }

  private loadResponseLists(): void {
    if (this.responseLists) {
      return;
    }
    this.responseListsService
      .getResponseListsWithStatistics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lists) => (this.responseLists = (lists ?? []) as unknown as ResponseListItem[]),
        error: () => this.snackBarService.error('generic.errorOccurred'),
      });
  }
}
